import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import { useEffect, useState, useRef } from 'react';
import { Menu, X, LogOut, LayoutDashboard, Bell, Plane, Users, Calendar, MoreVertical, TrendingUp, Clock, ChevronDown, FileText, BarChart2 } from 'lucide-react';
import API_URL from '../config';
import { useFeatures } from '../FeaturesContext';
import { getStoredToken } from '../utils/auth';

const getVueJourneeDateState = (pathname) => {
  const match = pathname.match(/\/gestionShifts\/jour\/([0-9-]+)/);
  if (match) {
    const dateStr = match[1];
    const [year, month, day] = dateStr.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);
    return { date: dateObj.toISOString() };
  }
  return undefined;
};

const Layout = () => {
  const features = useFeatures();
  const { isAuthenticated, logout, isManager, isTeamLeaderOrAbove } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const { pendingCount, updatePendingCount, refreshTrigger } = useNotifications();
  const [expiringContractsCount, setExpiringContractsCount] = useState(0);

  const vueJourneeState = getVueJourneeDateState(location.pathname);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Fetch pending requests count for managers
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!isManager) return;
      
      try {
        const token = getStoredToken();
        if (!token) return;
        
        // Fetch pending shift requests
        const shiftRes = await fetch(`${API_URL}/requests/?status=pending&limit=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Fetch pending leave requests
        const leaveRes = await fetch(`${API_URL}/api/leaves/team-requests?status_filter=pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (shiftRes.ok && leaveRes.ok) {
          const shiftData = await shiftRes.json();
          const leaveData = await leaveRes.json();
          const count = shiftData.length + leaveData.length;
          updatePendingCount(count);
        }
      } catch (error) {
        console.error('Error fetching pending count:', error);
      }
    };
    
    fetchPendingCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [isManager, updatePendingCount, refreshTrigger]);

  // Fetch expiring CDD contracts count (< 14 days)
  useEffect(() => {
    const fetchExpiringContracts = async () => {
      if (!isManager) return;
      try {
        const token = getStoredToken();
        if (!token) return;
        const res = await fetch(`${API_URL}/users/expiring-contracts-count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const { count } = await res.json();
        setExpiringContractsCount(count);
      } catch { /* silent */ }
    };

    fetchExpiringContracts();
    const interval = setInterval(fetchExpiringContracts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isManager]);

  const navGroups = [
    {
      title: 'Planning & Temps',
      items: [
        { name: 'Vue Hebdomadaire', path: '/gestionShifts', icon: LayoutDashboard },
        { name: 'Vue Mensuelle', path: '/vueMensuelle', icon: Calendar, teamLeaderAndAbove: true },
        { name: 'Congés', path: '/conges', icon: Plane },
      ]
    },
    {
      title: 'RH & Admin',
      items: [
        { name: 'Demandes', path: '/demandes', icon: Bell, managerOnly: true, badge: pendingCount },
        { name: 'Soldes Congés', path: '/soldesConges', icon: BarChart2, managerOnly: true },
        { name: 'Heures Sup', path: '/heuresSup', icon: Clock },
        ...(features.payslips ? [{ name: 'Fiches de Paie', path: '/payslips', icon: FileText, managerOnly: true }] : []),
        { name: 'Utilisateurs', path: '/utilisateurs', icon: Users, managerOnly: true, badge: expiringContractsCount },
      ]
    },
    {
      title: 'Performances',
      items: [
        { name: 'Analyse', path: '/analyse', icon: TrendingUp, managerOnly: true },
      ]
    }
  ].map(group => ({
    ...group,
    items: group.items.filter(link =>
      (!link.managerOnly || isManager) &&
      (!link.teamLeaderAndAbove || isTeamLeaderOrAbove)
    )
  })).filter(group => group.items.length > 0);

  const isActive = (path) => location.pathname === path;

  // Helper component for desktop dropdowns
  const DesktopDropdown = ({ group }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const hasActiveChild = group.items.some(item => isActive(item.path));

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <div 
        className="relative group mr-2"
        ref={dropdownRef}
      >
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            hasActiveChild || isOpen
              ? 'bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          {group.title}
          <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${isOpen ? 'rotate-180 text-indigo-600' : 'text-gray-400'}`} />
          {/* Badge sum for group */}
          {group.items.reduce((sum, item) => sum + (item.badge || 0), 0) > 0 && (
            <span className="ml-1.5 w-2 h-2 rounded-full bg-red-500"></span>
          )}
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 transform origin-top animate-in fade-in slide-in-from-top-2">
            {group.items.map(link => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  state={link.path === '/gestionShifts' ? vueJourneeState : undefined}
                  className={`flex items-center px-4 py-2.5 text-sm transition-colors ${
                    isActive(link.path) ? 'bg-indigo-50/50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon className={`w-4 h-4 mr-3 ${isActive(link.path) ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <span className="flex-1">{link.name}</span>
                  {link.badge > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] leading-none font-bold text-white bg-red-500 rounded-full">
                      {link.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-gray-100">
        <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/gestionShifts" state={vueJourneeState} className="flex items-center gap-3">
                <img 
                  src="/logo.png" 
                  alt="PlannMe Logo" 
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex flex-col">
                  <span className="text-2xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600">
                    PlannMe
                  </span>
                  <span className="text-[9px] font-medium text-gray-500 -mt-1 tracking-widest">
                    TEAM PLANNING
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation - Full width display on large screens */}
            <nav className="hidden lg:flex items-center gap-1">
              {isAuthenticated ? (
                <>
                  {navGroups.map((group, idx) => (
                    <DesktopDropdown key={idx} group={group} />
                  ))}
                  
                  <div className="h-5 w-px bg-gray-200 mx-1 flex-shrink-0"></div>

                  <button
                    onClick={logout}
                    className="flex items-center px-2.5 py-1.5 text-[13px] font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <LogOut className="w-4 h-4 mr-1.5 flex-shrink-0" />
                    Déconnexion
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Connexion
                </Link>
              )}
            </nav>

            {/* Medium Screen Navigation - Dropdown Menu */}
            <nav className="hidden md:flex lg:hidden items-center space-x-2">
              {isAuthenticated ? (
                <>
                  <div className="relative">
                    <button
                      onClick={() => setIsNavMenuOpen(!isNavMenuOpen)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium"
                      aria-label="Menu de navigation"
                    >
                      <MoreVertical className="w-4 h-4" />
                      <span className="text-sm">Menu</span>
                      {isManager && pendingCount > 0 && (
                        <span className="px-1.5 py-0.5 text-[10px] leading-none font-bold text-indigo-600 bg-white rounded-full">
                          {pendingCount}
                        </span>
                      )}
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isNavMenuOpen && (
                      <>
                        {/* Backdrop to close menu */}
                        <div 
                          className="fixed inset-0 z-30" 
                          onClick={() => setIsNavMenuOpen(false)}
                        />
                        
                        {/* Menu Items */}
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-40">
                          {navGroups.map((group, gIdx) => (
                            <div key={gIdx} className="mb-2 last:mb-0">
                              <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                {group.title}
                              </div>
                              {group.items.map((link) => {
                                const Icon = link.icon;
                                return (
                                  <Link
                                    key={link.path}
                                    to={link.path}
                                    state={link.path === '/gestionShifts' ? vueJourneeState : undefined}
                                    onClick={() => setIsNavMenuOpen(false)}
                                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-indigo-50 transition-colors ${
                                      isActive(link.path) ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                                    }`}
                                  >
                                    <Icon className={`w-5 h-5 ${isActive(link.path) ? 'text-indigo-600' : 'text-gray-400'}`} />
                                    <span className="font-medium flex-1 text-sm">{link.name}</span>
                                    {link.badge > 0 && (
                                      <span className="px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                                        {link.badge}
                                      </span>
                                    )}
                                  </Link>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="h-6 w-px bg-gray-200"></div>

                  <button
                    onClick={logout}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Connexion
                </Link>
              )}
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-100 shadow-lg">
            {isAuthenticated ? (
              <>
                {navGroups.map((group, gIdx) => (
                  <div key={gIdx} className="mb-4 last:mb-0">
                    <div className="px-3 mb-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                      {group.title}
                    </div>
                    {group.items.map((link) => {
                      const Icon = link.icon;
                      return (
                        <Link
                          key={link.path}
                          to={link.path}
                          state={link.path === '/gestionShifts' ? vueJourneeState : undefined}
                          className={`flex items-center px-3 py-2.5 mb-1 rounded-lg text-sm font-medium ${
                            isActive(link.path)
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mr-3 ${isActive(link.path) ? 'text-indigo-600' : 'text-gray-400'}`} />
                          {link.name}
                          {link.badge > 0 && (
                            <span className="ml-auto px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                              {link.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                ))}
                <div className="border-t border-gray-100 my-2 pt-2">
                  <button
                    onClick={logout}
                    className="flex w-full items-center px-3 py-3 rounded-md text-base font-medium text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-5 h-5 mr-3" />
                    Déconnexion
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="block w-full text-center px-4 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm"
              >
                Connexion
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-grow">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
